import { useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Box, Grid, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import {
  ApartmentOutlined,
  EngineeringOutlined,
  GroupOutlined,
  HeadsetMicOutlined,
  ReplayOutlined,
  SearchOutlined,
  SupervisorAccountOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  Bar,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import CustomTableComponent from "../../../components/customTableComponent";
import BarChartComponent from "../../../components/chartComponents/barChartComponent";
import RankingBarTableComponent from "../../../components/chartComponents/rankingBarTableComponent";
import {
  ChartCard,
  MetricCard,
  formatInt,
  formatPct,
  formatTempo,
} from "./shared";
import {
  DistribuicaoChart,
  QuadranteChart,
  WilsonBar,
  pctColor,
  wilsonLower,
} from "./analytics";
import {
  EfeitoTecnicoCidade,
  FinanceiroFilial,
  MapaReincidencia,
  TmtScatter,
} from "./domainCharts";
import { GUIDES, MetricInfo } from "./metricInfo";
import { RankingVariacao, SimChip } from "./simCharts";
import { atendentesTrend } from "./simulatedData";
import { maxWidth } from "@mui/system";

// Reincidência média ponderada pelo volume.
const weightedPct = (rows) => {
  const vol = rows.reduce((s, r) => s + (r.volume || 0), 0);
  const reinc = rows.reduce((s, r) => s + (r.reinc || 0), 0);
  return vol ? reinc / vol : 0;
};

const avgTmt = (rows) => {
  const valid = rows.filter((r) => r.tmt);
  if (!valid.length) return 0;
  return valid.reduce((s, r) => s + r.tmt, 0) / valid.length;
};

// Agrupa por chave e devolve [{ grupo, volume, reinc, pct }] ordenado por pct desc.
const agruparPorChave = (rows, chave) => {
  const map = new Map();
  rows.forEach((r) => {
    const k = r[chave] || "—";
    const acc = map.get(k) || { grupo: k, volume: 0, reinc: 0 };
    acc.volume += r.volume || 0;
    acc.reinc += r.reinc || 0;
    map.set(k, acc);
  });
  return [...map.values()]
    .map((g) => ({ ...g, pct: g.volume ? g.reinc / g.volume : 0 }))
    .sort((a, b) => b.pct - a.pct);
};

const shortName = (nome, max = 26) => {
  const s = String(nome || "").replace(/^[A-Z]{2}\d{2}-[A-Z-]+\s*-?\s*/i, "");
  const base = s || String(nome || "");
  return base.length > max ? `${base.slice(0, max)}…` : base;
};

/* ------------------------------ charts ------------------------------ */
// Barra horizontal: top N por % de reincidência (com filtro de volume mínimo).
function RankingBar({ styles, theme, rows, getLabel, minVolume, topN = 10 }) {
  const data = useMemo(() => {
    return rows
      .filter((r) => (r.volume || 0) >= minVolume)
      .sort((a, b) => b.pct - a.pct)
      .slice(0, topN)
      .map((r) => {
        const pct = +(r.pct * 100).toFixed(1);
        return {
          label: getLabel(r),
          labelTooltip: r.nome,
          value: pct,
          color: pctColor(theme, r.pct),
          tooltip: (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {getLabel(r)}
              </Typography>
              <Typography variant="body2">
                {`${pct}%  •  ${formatInt(r.volume)} chamados`}
              </Typography>
            </>
          ),
        };
      });
  }, [rows, getLabel, minVolume, topN, theme]);

  return (
    <Box sx={styles.chartBox}>
      <RankingBarTableComponent rows={data} valueFormatter={(v) => `${v}%`} />
    </Box>
  );
}

// Barra vertical: % de reincidência por grupo (filial / setor).
function GrupoBar({ styles, theme, data }) {
  const chartData = data.map((d) => ({ ...d, color: pctColor(theme, d.pct) }));
  return (
    <Box sx={styles.chartBox}>
      <BarChartComponent
        data={chartData}
        orientation="vertical"
        categoryKey="grupo"
        valueKey="pctNum"
        valueFormatter={(v) => `${v}%`}
        barSize={34}
        categoryAngle={data.length > 6 ? -25 : 0}
        renderTooltipContent={(item) => (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item?.payload?.grupo}
            </Typography>
            <Typography variant="body2">
              {`${item?.value}%  •  ${formatInt(item?.payload?.volume)} chamados`}
            </Typography>
          </>
        )}
      />
    </Box>
  );
}

// Pareto: contribuição acumulada para a reincidência total (regra 80/20).
function ParetoChart({ styles, theme, rows, getLabel, topN = 15 }) {
  const data = useMemo(() => {
    const totalReinc = rows.reduce((s, r) => s + (r.reinc || 0), 0) || 1;
    const ordenado = [...rows].sort((a, b) => (b.reinc || 0) - (a.reinc || 0));
    let acc = 0;
    return ordenado.slice(0, topN).map((r) => {
      acc += r.reinc || 0;
      return {
        label: getLabel(r),
        reinc: r.reinc || 0,
        acumulado: +((acc / totalReinc) * 100).toFixed(1),
      };
    });
  }, [rows, getLabel, topN]);

  // Linha de acumulado em amarelo: o secondary.main some no modo escuro.
  const corAcumulado = "#FFC107";

  return (
    <Box sx={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 50 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
            interval={0}
            angle={-35}
            textAnchor="end"
            height={60}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            formatter={(value, name) =>
              name === "acumulado"
                ? [`${value}%`, "Acumulado"]
                : [formatInt(value), "Reincidências"]
            }
            contentStyle={styles.chartTooltip}
            cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
          />
          <Bar
            yAxisId="left"
            dataKey="reinc"
            fill={theme.palette.primary.main}
            radius={[6, 6, 0, 0]}
            barSize={16}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="acumulado"
            stroke={corAcumulado}
            strokeWidth={2}
            dot={{ r: 3, fill: corAcumulado, strokeWidth: 0 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

// Tabela customizada para o ranking detalhado (técnicos ou atendentes).
function RankingTable({ theme, rows, extraColumns = [] }) {
  const [busca, setBusca] = useState("");
  const filtered = useMemo(() => {
    if (!busca) return rows;
    const term = busca.toLowerCase();
    return rows.filter((r) => r.nome?.toLowerCase().includes(term));
  }, [rows, busca]);

  const cols = [
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      minWidth: 200,

      renderCell: (p) => (
        <Tooltip title={p.value} placement="right">
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 500, maxWidth: 280, display: "block" }}
          >
            {p.value}
          </Typography>
        </Tooltip>
      ),
    },
    ...extraColumns,
    {
      field: "volume",
      headerName: "Volume Total",
      width: 120,
      type: "number",
      renderCell: (p) => <Typography variant="body2">{formatInt(p.value)}</Typography>,
    },
    {
      field: "reinc",
      headerName: "Reincidências",
      width: 120,
      type: "number",
      renderCell: (p) => <Typography variant="body2">{formatInt(p.value)}</Typography>,
    },
    {
      field: "pct",
      headerName: "Reincidência (%)",
      width: 140,
      type: "number",
      renderCell: (p) => {
        const value = p.row.volume > 0 ? p.row.reinc / p.row.volume : 0;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "flex-end" }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: pctColor(theme, value),
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatPct(value)}
            </Typography>
          </Box>
        );
      },
      sortComparator: (v1, v2, c1, c2) => {
        const p1 = c1.api.getRow(c1.id).volume > 0 ? c1.api.getRow(c1.id).reinc / c1.api.getRow(c1.id).volume : 0;
        const p2 = c2.api.getRow(c2.id).volume > 0 ? c2.api.getRow(c2.id).reinc / c2.api.getRow(c2.id).volume : 0;
        return p1 - p2;
      },
    },
    {
      field: "confianca",
      headerName: "Pior Caso (Wilson)",
      width: 150,
      type: "number",
      renderCell: (p) => {
        const value = wilsonLower(p.row.reinc, p.row.volume);
        return (
          <Tooltip title="Limite inferior de confiança (95%). Usa estatística para penalizar amostras pequenas.">
            <Typography variant="body2" color="text.secondary">
              {formatPct(value)}
            </Typography>
          </Tooltip>
        );
      },
      sortComparator: (v1, v2, c1, c2) => {
        const p1 = wilsonLower(c1.api.getRow(c1.id).reinc, c1.api.getRow(c1.id).volume);
        const p2 = wilsonLower(c2.api.getRow(c2.id).reinc, c2.api.getRow(c2.id).volume);
        return p1 - p2;
      },
    },
    {
      field: "tmt",
      headerName: "TMT Médio",
      width: 130,
      type: "number",
      renderCell: (p) => (
        <Typography variant="body2" color="text.secondary">
          {formatTempo(p.value)}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: "800px", gap: 2 }}>
      <TextField
        size="small"
        placeholder="Buscar por nome..."
        variant="outlined"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        sx={{ maxWidth: 300 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ flexGrow: 1, minHeight: 400 }}>
        <CustomTableComponent
          rows={filtered.map((r, i) => ({ id: i, ...r }))}
          columns={cols}
          density="compact"
          disableColumnMenu
          disableSelectionOnClick
        />
      </Box>
    </Box>
  );
}

/* ============================== CAMPO ============================== */
export function MicrogestaoCampoTab({ styles, theme, tecnicos = [], regionais = [], loading = false }) {
  const totalReinc = tecnicos.reduce((s, r) => s + (r.reinc || 0), 0);
  const pctMedio = weightedPct(tecnicos);
  // Pior técnico considerando um volume mínimo (ex: >= 5).
  const piorTecnico = [...tecnicos]
    .filter((r) => (r.volume || 0) >= 5)
    .sort((a, b) => b.pct - a.pct)[0];
  const tmtGeral = avgTmt(tecnicos);

  const temTecnicos = tecnicos.length > 0;
  const temReincidencia = tecnicos.some((r) => (r.reinc || 0) > 0);
  const temTmt = tecnicos.some((r) => r.tmt && (r.volume || 0) >= 5);
  const showSkeleton = loading && !tecnicos.length;

  return (
    <Grid
      container
      spacing={1}
      columns={{ xs: 4, sm: 8, md: 12, lg: 20, xl: 20 }}
      sx={{ maxWidth: "100%", minWidth: 0 }}
    >
      <Grid item xs={4} sm={4} md={4} lg={5}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={EngineeringOutlined}
          accent={theme.palette.primary.main}
          title="Técnicos em campo"
          value={formatInt(tecnicos.length)}
          helper={`${formatInt(totalReinc)} reincidências`}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={5}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={ReplayOutlined}
          accent={pctColor(theme, pctMedio)}
          title="Reincidência média"
          value={formatPct(pctMedio)}
          helper="Global dos técnicos"
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={5}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={GroupOutlined}
          accent={theme.palette.error.main}
          title="Técnico mais crítico"
          value={piorTecnico ? formatPct(piorTecnico.pct) : "-"}
          helper={piorTecnico ? shortName(piorTecnico.nome, 22) : "-"}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={5}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={TimerOutlined}
          accent={theme.palette.info.main}
          title="TMT Médio"
          value={formatTempo(tmtGeral)}
          helper="Tempo Médio de Trabalho"
        />
      </Grid>

      {/* 1) Quem reincide mais — ranking direto */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Top 10 Técnicos por Reincidência (%)"
          subtitle="Quem mais reincide — apenas técnicos com ≥ 5 OS no período"
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos com o volume mínimo para o ranking."
        >
          <RankingBar
            styles={styles}
            theme={theme}
            rows={tecnicos}
            getLabel={(r) => shortName(r.nome, 20)}
            minVolume={5}
            topN={10}
          />
        </ChartCard>
      </Grid>

      {/* 2) Ranking ajustado — corrige o viés de amostras pequenas */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Ranking por Confiança (Wilson 95%)"
          subtitle="De quem temos certeza que reincide — ajustado para amostras pequenas"
          topRight={<MetricInfo theme={theme} guide={GUIDES.wilson} />}
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos para calcular o intervalo de Wilson."
        >
          <WilsonBar
            styles={styles}
            theme={theme}
            rows={tecnicos}
            getLabel={(r) => shortName(r.nome, 24)}
          />
        </ChartCard>
      </Grid>

      {/* 3) Onde priorizar — volume × gravidade */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Matriz Volume × Reincidência"
          subtitle="Onde priorizar — quadrante superior direito = prioridade máxima"
          topRight={<MetricInfo theme={theme} guide={GUIDES.quadrante} />}
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos para montar a matriz."
        >
          <QuadranteChart
            styles={styles}
            theme={theme}
            rows={tecnicos}
            getLabel={(r) => shortName(r.nome, 24)}
          />
        </ChartCard>
      </Grid>

      {/* 4) Concentração — poucos técnicos = maioria das reincidências */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Pareto de Técnicos (80/20)"
          subtitle="Concentração — quanto cada técnico acumula do total de reincidências"
          topRight={<MetricInfo theme={theme} guide={GUIDES.pareto} />}
          noData={!temReincidencia}
          noDataMessage="Não foram encontradas reincidências para montar o Pareto."
        >
          <ParetoChart
            styles={styles}
            theme={theme}
            rows={tecnicos}
            getLabel={(r) => shortName(r.nome, 16)}
          />
        </ChartCard>
      </Grid>

      {/* 5) Panorama do time — distribuição por faixa de risco */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Distribuição de Reincidência"
          subtitle="Panorama do time — quantos técnicos por faixa de risco"
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos para calcular a distribuição."
        >
          <DistribuicaoChart styles={styles} theme={theme} rows={tecnicos} />
        </ChartCard>
      </Grid>

      {/* 6) Possível causa — tempo de atendimento × reincidência */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Correlação: TMT vs Reincidência"
          subtitle="Possível causa — atendimento apressado reincide mais?"
          topRight={<MetricInfo theme={theme} guide={GUIDES.tmt} />}
          noData={!temTmt}
          noDataMessage="Não há técnicos com TMT suficiente para montar a dispersão."
        >
          <TmtScatter styles={styles} theme={theme} rows={tecnicos} />
        </ChartCard>
      </Grid>


      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Efeito técnico × rede por filial"
          subtitle="Dispersão entre técnicos: pessoa vs infra"
          topRight={<MetricInfo theme={theme} guide={GUIDES.efeito} />}
        >
          <EfeitoTecnicoCidade styles={styles} theme={theme} />
        </ChartCard>
      </Grid>
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Mapa de reincidência por cidade"
          subtitle="Tamanho = volume • cor = faixa de reincidência"
          emptyHeight={446}
        >
          <MapaReincidencia styles={styles} theme={theme} liveRegionais={regionais} />
        </ChartCard>
      </Grid>
      <Grid item xs={4} sm={8} md={12} lg={20}>

        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Custo de retrabalho por filial"
          subtitle="Reincidências × custo médio por OS"
        >
          <FinanceiroFilial styles={styles} theme={theme} />
        </ChartCard>
      </Grid>

      <Grid item xs={4} sm={8} md={12} lg={20}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Ranking detalhado de técnicos"
          noData={!temTecnicos}
          noDataMessage="Não foram encontrados técnicos no período selecionado."
          emptyHeight={480}
        >
          <RankingTable
            theme={theme}
            rows={[...tecnicos].sort((a, b) => b.pct - a.pct)}
          />
        </ChartCard>
      </Grid>
    </Grid>
  );
}

/* ============================== ATENDIMENTO ============================== */
export function MicrogestaoAtendimentoTab({
  styles,
  theme,
  atendentes = [],
  setores = [],
  loading = false,
}) {
  const totalReinc = atendentes.reduce((s, r) => s + (r.reinc || 0), 0);
  const pctMedio = weightedPct(atendentes);
  const piorAtendente = [...atendentes]
    .filter((r) => (r.volume || 0) >= 5)
    .sort((a, b) => b.pct - a.pct)[0];

  // Setor vem da API (/reincidencia/setor) — agregado por OS via atendimento.
  const porSetor = useMemo(
    () =>
      [...setores]
        .sort((a, b) => (b.pct || 0) - (a.pct || 0))
        .map((g) => ({ ...g, pctNum: +((g.pct || 0) * 100).toFixed(1) })),
    [setores],
  );
  const piorSetor = porSetor[0];
  const porSupervisor = useMemo(
    () =>
      agruparPorChave(atendentes, "supervisor")
        .filter((g) => g.volume >= 5)
        .slice(0, 12),
    [atendentes],
  );

  const extraColumns = [
    { field: "setor", headerName: "Setor", width: 150 },
    { field: "supervisor", headerName: "Supervisor", flex: 1, minWidth: 180 },
  ];
  const temAtendentes = atendentes.length > 0;
  const temRankingAtendentes = atendentes.some((r) => (r.volume || 0) >= 5);
  const temReincidenciaAtendentes = atendentes.some((r) => (r.reinc || 0) > 0);
  const temTmtAtendentes = atendentes.some((r) => r.tmt && (r.volume || 0) >= 5);
  const showSkeleton = loading && !atendentes.length;

  return (
    <Grid
      container
      spacing={1}
      columns={{ xs: 4, sm: 8, md: 12, lg: 20, xl: 20 }}
      sx={{ maxWidth: "100%", minWidth: 0 }}
    >
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={HeadsetMicOutlined}
          accent={theme.palette.primary.main}
          title="Atendentes avaliados"
          value={formatInt(atendentes.length)}
          helper={`${formatInt(porSetor.length)} setores`}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={ReplayOutlined}
          accent={pctColor(theme, pctMedio)}
          title="Reincidência média"
          value={formatPct(pctMedio)}
          helper={`${formatInt(totalReinc)} reincidências`}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={GroupOutlined}
          accent={theme.palette.error.main}
          title="Atendente crítico (vol ≥ 5)"
          value={piorAtendente ? formatPct(piorAtendente.pct) : "-"}
          helper={piorAtendente ? shortName(piorAtendente.nome, 22) : "-"}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={ApartmentOutlined}
          accent={theme.palette.warning.main}
          title="Setor crítico"
          value={piorSetor ? formatPct(piorSetor.pct) : "-"}
          helper={piorSetor ? piorSetor.grupo : "-"}
        />
      </Grid>
      <Grid item xs={4} sm={4} md={4} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          icon={TimerOutlined}
          accent={theme.palette.info.main}
          title="TMT médio"
          value={formatTempo(avgTmt(atendentes))}
          helper="Tempo médio de tratamento"
        />
      </Grid>

      {/* 1) Quem reincide mais — ranking direto */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Top atendentes por reincidência"
          subtitle="Quem mais reincide — volume ≥ 5 chamados"
          noData={!temRankingAtendentes}
          noDataMessage="Não foram encontrados atendentes com volume mínimo para montar este ranking."
        >
          <RankingBar
            styles={styles}
            theme={theme}
            rows={atendentes}
            getLabel={(r) => shortName(r.nome, 22)}
            minVolume={5}
          />
        </ChartCard>
      </Grid>

      {/* 2) Ranking ajustado — corrige o viés de amostras pequenas */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Ranking por Confiança (Wilson 95%)"
          subtitle="De quem temos certeza que reincide — ajustado para amostras pequenas"
          topRight={<MetricInfo theme={theme} guide={GUIDES.wilson} />}
          noData={!temAtendentes}
          noDataMessage="Não foram encontrados atendentes para calcular o ranking por confiança."
        >
          <WilsonBar
            styles={styles}
            theme={theme}
            rows={atendentes}
            getLabel={(r) => shortName(r.nome, 24)}
          />
        </ChartCard>
      </Grid>

      {/* 3) Onde priorizar — volume × gravidade */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Matriz Volume × Reincidência"
          subtitle="Onde priorizar — quadrante superior direito = prioridade máxima"
          topRight={<MetricInfo theme={theme} guide={GUIDES.quadrante} />}
          noData={!temAtendentes}
          noDataMessage="Não foram encontrados atendentes para montar a matriz."
        >
          <QuadranteChart
            styles={styles}
            theme={theme}
            rows={atendentes}
            getLabel={(r) => shortName(r.nome, 24)}
          />
        </ChartCard>
      </Grid>

      {/* 4) Concentração — poucos atendentes = maioria das reincidências */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Pareto de atendentes (80/20)"
          subtitle="Concentração — quanto cada atendente acumula do total"
          topRight={<MetricInfo theme={theme} guide={GUIDES.pareto} />}
          noData={!temReincidenciaAtendentes}
          noDataMessage="Não foram encontradas reincidências para montar o Pareto."
        >
          <ParetoChart
            styles={styles}
            theme={theme}
            rows={atendentes}
            getLabel={(r) => shortName(r.nome, 14)}
            topN={12}
          />
        </ChartCard>
      </Grid>

      {/* 5) Recortes organizacionais — onde a reincidência se concentra */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Reincidência por setor"
          subtitle="Recorte organizacional — índice ponderado por volume"
          noData={!porSetor.length}
          noDataMessage="Não foram encontrados setores para calcular a reincidência."
        >
          <GrupoBar styles={styles} theme={theme} data={porSetor} />
        </ChartCard>
      </Grid>
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Reincidência por supervisor"
          subtitle="Recorte organizacional — volume ≥ 5, top 12"
          topRight={<SupervisorAccountOutlined sx={{ color: theme.palette.text.secondary }} />}
          noData={true}
          noDataMessage="Não foram encontrados supervisores com volume mínimo para este gráfico."
        >
          <RankingBar
            styles={styles}
            theme={theme}
            rows={porSupervisor.map((g) => ({ ...g, nome: g.grupo }))}
            getLabel={(r) => shortName(r.grupo, 24)}
            minVolume={0}
            topN={12}
          />
        </ChartCard>
      </Grid>

      {/* 6) Panorama do time — distribuição por faixa de risco */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Distribuição dos atendentes"
          subtitle="Panorama do time — quantos atendentes por faixa de reincidência"
          noData={!temAtendentes}
          noDataMessage="Não foram encontrados atendentes para calcular a distribuição."
        >
          <DistribuicaoChart styles={styles} theme={theme} rows={atendentes} />
        </ChartCard>
      </Grid>

      {/* 7) Possível causa — tempo de atendimento × reincidência */}
      <Grid item xs={4} sm={8} md={12} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Correlação: TMT vs Reincidência"
          subtitle="Possível causa — atendimento apressado reincide mais?"
          topRight={<MetricInfo theme={theme} guide={GUIDES.tmt} />}
          noData={!temTmtAtendentes}
          noDataMessage="Não há atendentes com TMT e volume suficiente para montar a dispersão."
        >
          <TmtScatter styles={styles} theme={theme} rows={atendentes} />
        </ChartCard>
      </Grid>

      {/* 8) Acompanhamento (simulado) — variação mensal */}
      <Grid item xs={4} sm={8} md={12} lg={20}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Variação mensal — atendentes"
          subtitle="Quem mais piorou (vermelho) ou melhorou (verde) — Mar→Mai"
          topRight={<SimChip theme={theme} />}
          noData={!atendentesTrend.length}
          noDataMessage="Não há dados de tendência para montar a variação mensal."
        >
          <RankingVariacao styles={styles} theme={theme} trend={atendentesTrend} />
        </ChartCard>
      </Grid>

      <Grid item xs={4} sm={8} md={12} lg={20}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={showSkeleton}
          title="Ranking completo de atendentes"
          subtitle={`${formatInt(atendentes.length)} atendentes`}
          noData={!temAtendentes}
          noDataMessage="Não foram encontrados atendentes no período selecionado."
          emptyHeight={480}
        >
          <RankingTable
            theme={theme}
            rows={[...atendentes].sort((a, b) => b.pct - a.pct)}
            extraColumns={extraColumns}
          />
        </ChartCard>
      </Grid>
    </Grid>
  );
}
